import React, { useState, useEffect } from "react";
import CalculatorImg from "../../assets/images/Calculator.png";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { getPublicSettings } from '../../services/settingsService';
import { getDoorAndWindowPrices } from '../../services/settingsService';

const Calculator = () => {
  const { t } = useTranslation();
  document.title = t("calculator.0");
  const headerTitle = t("calculator.0");
  const backwardNavigate = "/home";
  const navigate = useNavigate();
  
  // Load stored data or initialize with defaults
  const storedData = JSON.parse(sessionStorage.getItem("homeData")) || {
    areaHouse: "",
    heightWall: "",
    roomCount: 0,
    roomDoorCount: 0,
    windowCount: 0,
  };

  const [inputData, setInputData] = useState({
    areaHouse: storedData.areaHouse,
    heightWall: storedData.heightWall,
  });
  const [roomCount, setRoomCount] = useState(storedData.roomCount);
  const [roomDoorCount, setRoomDoorCount] = useState(storedData.roomDoorCount);
  const [windowCount, setWindowCount] = useState(storedData.windowCount);
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState(null);

  // Fetch public settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getPublicSettings();
        setSettings(response.data || {});
        console.log('Loaded settings:', response.data);
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error(t("calculator.settingsError"));
      }
    };
    fetchSettings();
  }, [t]);

  // Fetch door and window prices based on style and tariff
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // Get style and tariff from sessionStorage and properly parse them
        let styleId, tariffId;
        
        // Get and parse style
        const storedStyle = sessionStorage.getItem("selectedRepairStyle");
        console.log("Stored style:", storedStyle);
        
        if (storedStyle) {
          try {
            const parsedStyle = JSON.parse(storedStyle);
            styleId = parsedStyle.id || parsedStyle._id || parsedStyle;
          } catch (e) {
            // If not JSON, use as is (string ID)
            styleId = storedStyle;
          }
        }
        
        // Get and parse tariff
        const storedTariff = sessionStorage.getItem("selectedRepairTariff");
        console.log("Stored tariff:", storedTariff);
        
        if (storedTariff) {
          try {
            const parsedTariff = JSON.parse(storedTariff);
            tariffId = parsedTariff.id || parsedTariff._id || parsedTariff;
          } catch (e) {
            // If not JSON, use as is (string ID)
            tariffId = storedTariff;
          }
        }
        
        console.log("Parsed IDs:", { styleId, tariffId });
        
        if (styleId && tariffId) {
          console.log("Making API call for door/window prices...");
          const response = await getDoorAndWindowPrices(styleId, tariffId);
          console.log("API response:", response);
          if (response && response.prices) {
            console.log("Setting prices from API:", response.prices);
            setSettings(prev => ({
              ...prev,
              doorPrice: parseFloat(response.prices.door.price) || 64,
              doorServicePrice: parseFloat(response.prices.door.servicePrice) || 24,
              windowPrice: parseFloat(response.prices.window.price) || 48,
              windowServicePrice: parseFloat(response.prices.window.servicePrice) || 16
            }));
          } else {
            console.log("No valid response data, using defaults");
          }
        } else {
          console.log("Missing style or tariff ID, cannot fetch prices");
        }
      } catch (error) {
        console.error('Error fetching door/window prices:', error);
        toast.error(t("calculator.priceError"));
      }
    };
    
    fetchPrices();
  }, [t]);

  const validateInput = (name, value) => {
    if (value === "" || value === null) return false;
    const numValue = parseFloat(value);
    
    switch (name) {
      case 'areaHouse':
        return numValue > 0 && numValue <= 1000; // Max 1000m²
      case 'heightWall':
        return numValue >= 2 && numValue <= 5; // Between 2m and 5m
      case 'roomCount':
        return numValue > 0 && numValue <= 20; // Max 20 rooms
      case 'roomDoorCount':
        return numValue >= 0 && numValue <= 50; // Max 50 doors
      case 'windowCount':
        return numValue >= 0 && numValue <= 50; // Max 50 windows
      default:
        return true;
    }
  };

  const handleData = (e) => {
    const { name, value } = e.target;
    const numValue = value ? parseFloat(value) : "";
    
    if (value && !validateInput(name, numValue)) {
      setErrors(prev => ({ ...prev, [name]: true }));
      toast.error(t(`calculator.${name}Error`));
      return;
    }

    setInputData(prev => ({ ...prev, [name]: numValue }));
    setErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleSubmit = () => {
    const newErrors = {};
    let hasErrors = false;

    // Validate all fields
    ['areaHouse', 'heightWall'].forEach(field => {
      if (!validateInput(field, inputData[field])) {
        newErrors[field] = true;
        hasErrors = true;
      }
    });

    if (!validateInput('roomCount', roomCount)) {
      newErrors.roomCount = true;
      hasErrors = true;
    }

    if (!validateInput('roomDoorCount', roomDoorCount)) {
      newErrors.roomDoorCount = true;
      hasErrors = true;
    }

    if (!validateInput('windowCount', windowCount)) {
      newErrors.windowCount = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error(t("calculator.validationError"));
      return;
    }

    // Save data including settings prices
    const allData = {
      areaHouse: inputData.areaHouse,
      heightWall: inputData.heightWall,
      roomCount: roomCount,
      roomDoorCount: roomDoorCount,
      windowCount: windowCount,
      settings: {
        doorPrice: settings?.doorPrice,
        doorServicePrice: settings?.doorServicePrice,
        windowPrice: settings?.windowPrice,
        windowServicePrice: settings?.windowServicePrice
      }
    };

    try {
      sessionStorage.setItem("homeData", JSON.stringify(allData));
      navigate("/dimensionroom");
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error(t("calculator.saveError"));
    }
  };

  // Rest of your existing handlers with validation
  const handleIncreaseRoomCount = () => {
    const newCount = roomCount + 1;
    if (validateInput('roomCount', newCount)) {
      setRoomCount(newCount);
      setErrors(prev => ({ ...prev, roomCount: false }));
    } else {
      toast.error(t("calculator.maxRoomsError"));
    }
  };

  const handleDecreaseRoomCount = () => {
    if (roomCount > 0) {
      setRoomCount(roomCount - 1);
      setErrors(prev => ({ ...prev, roomCount: false }));
    }
  };

  const handleIncreaseRoomDoorCount = () => {
    const newCount = roomDoorCount + 1;
    if (validateInput('roomDoorCount', newCount)) {
      setRoomDoorCount(newCount);
      setErrors(prev => ({ ...prev, roomDoorCount: false }));
    } else {
      toast.error(t("calculator.maxDoorsError"));
    }
  };

  const handleDecreaseRoomDoorCount = () => {
    if (roomDoorCount > 0) {
      setRoomDoorCount(roomDoorCount - 1);
      setErrors(prev => ({ ...prev, roomDoorCount: false }));
    }
  };

  const handleIncreaseWindowCount = () => {
    const newCount = windowCount + 1;
    if (validateInput('windowCount', newCount)) {
      setWindowCount(newCount);
      setErrors(prev => ({ ...prev, windowCount: false }));
    } else {
      toast.error(t("calculator.maxWindowsError"));
    }
  };

  const handleDecreaseWindowCount = () => {
    if (windowCount > 0) {
      setWindowCount(windowCount - 1);
      setErrors(prev => ({ ...prev, windowCount: false }));
    }
  };

  return (
    <div className="calculator-section mx-3">
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={() => navigate(backwardNavigate)}
      />
      <div className="top-section mb-5">
        <img src={CalculatorImg} alt="calculator" />
        <div className="text-section text-center">
          <h4>{t("calculator.1")}</h4>
        </div>
      </div>
      <div className="middle-section d-flex align-items-center justify-content-between">
        <form action="">
          <div className="row">
            <div className="col-6">
              <div className="area-of-house d-flex flex-column">
                <label className="mb-2">{t("calculator.2")} (m²) :</label>
                <input
                  type="number"
                  name="areaHouse"
                  value={inputData.areaHouse}
                  onChange={handleData}
                  placeholder="m²"
                  className={errors.areaHouse ? "error-input" : ""}
                  min="1"
                  max="1000"
                />
              </div>
            </div>
            <div className="col-6">
              <div className="height-of-wall d-flex flex-column">
                <label className="mb-2">{t("calculator.3")} (m) :</label>
                <input
                  type="number"
                  name="heightWall"
                  value={inputData.heightWall}
                  onChange={handleData}
                  placeholder="m"
                  className={errors.heightWall ? "error-input" : ""}
                  min="2"
                  max="5"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="bottom-section">
        <div className="bottom-top">
          <h4>{t("calculator.4")}</h4>
          <div className="d-flex align-items-center justify-content-between gap-3">
            <button 
              className="btn" 
              onClick={handleDecreaseRoomCount}
              disabled={roomCount === 0}
            >
              -
            </button>
            <p className={errors.roomCount ? "error-border" : ""}>
              {roomCount}
            </p>
            <button 
              className="btn" 
              onClick={handleIncreaseRoomCount}
              disabled={roomCount >= 20}
            >
              +
            </button>
          </div>
        </div>
        <div className="bottom-bottom">
          <h4>{t("calculator.5")}</h4>
          <div className="d-flex align-items-center justify-content-between gap-3">
            <button 
              className="btn" 
              onClick={handleDecreaseRoomDoorCount}
              disabled={roomDoorCount === 0}
            >
              -
            </button>
            <p className={errors.roomDoorCount ? "error-border" : ""}>
              {roomDoorCount}
            </p>
            <button 
              className="btn" 
              onClick={handleIncreaseRoomDoorCount}
              disabled={roomDoorCount >= 50}
            >
              +
            </button>
          </div>
        </div>
        <div className="bottom-bottom">
          <h4>{t("calculator.6")}</h4>
          <div className="d-flex align-items-center justify-content-between gap-3">
            <button 
              className="btn" 
              onClick={handleDecreaseWindowCount}
              disabled={windowCount === 0}
            >
              -
            </button>
            <p className={errors.windowCount ? "error-border" : ""}>
              {windowCount}
            </p>
            <button 
              className="btn" 
              onClick={handleIncreaseWindowCount}
              disabled={windowCount >= 50}
            >
              +
            </button>
          </div>
        </div>
      </div>
      <div className="button-section mb-2">
        <button
          type="button"
          className="btn py-3 global-button"
          onClick={handleSubmit}
          disabled={Object.values(errors).some(error => error)}
        >
          {t("calculator.8")}
        </button>
      </div>
    </div>
  );
};

export default Calculator;
